import { Banner, DataField, FieldGrid } from '@if/mui'
import { LinearProgress, Stack } from '@mui/material'
import { ELoadLoansRunStatus, isLoadLoansRunInProgress, type LoadLoansRun } from '@src/types/load-loans'
import { useTranslation } from 'react-i18next'

interface LoadLoansProgressProps {
  run: LoadLoansRun
}

/**
 * The latest run: how far it is, how fast it goes and what it produced. The elapsed time comes from
 * the server (real clock) and moves with each poll.
 */
const LoadLoansProgress = ({ run }: LoadLoansProgressProps) => {
  const { t } = useTranslation()
  const seconds = Math.max(1, run.elapsedSeconds)
  // A replayed batch can create more than requested: cap the bar, keep the count honest.
  const percent = run.requested > 0 ? Math.min(100, Math.round((run.created / run.requested) * 100)) : 0

  return (
    <Stack spacing={1}>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={run.status === ELoadLoansRunStatus.Failed ? 'error' : 'primary'}
      />
      <FieldGrid columns={{ xs: 2, md: 4 }}>
        <DataField
          label={t('admin.devTools.commands.loadLoans.status')}
          value={t(`admin.devTools.commands.loadLoans.statuses.${run.status}`)}
        />
        <DataField
          label={t('admin.devTools.commands.loadLoans.progress')}
          value={`${run.created} / ${run.requested}`}
        />
        <DataField label={t('admin.devTools.commands.loadLoans.elapsed')} value={`${run.elapsedSeconds} s`} />
        <DataField
          label={t('admin.devTools.commands.loadLoans.throughput')}
          value={`${Math.round(run.created / seconds)} / s`}
        />
      </FieldGrid>
      {run.firstContractNumber && (
        <DataField
          label={t('admin.devTools.commands.loadLoans.contractRange')}
          value={`${run.firstContractNumber} – ${run.lastContractNumber}`}
        />
      )}
      {run.errorMessage && <Banner severity="error">{run.errorMessage}</Banner>}
      {isLoadLoansRunInProgress(run) && (
        <Banner severity="info">{t('admin.devTools.commands.loadLoans.inProgress')}</Banner>
      )}
    </Stack>
  )
}

export default LoadLoansProgress
